import {
  Button,
  ButtonProps,
  Menu,
  MenuProps,
  Select,
  SelectProps,
  Spinner,
  Stack,
  useColorMode,
  useColorModeValue as mode,
  useTheme,
} from '@chakra-ui/react';
import { transparentize as t } from '@chakra-ui/theme-tools';
import { Omit } from 'lodash';
import { ChangeEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Option } from './SimpleSelect';

type Props = Omit<SelectProps | MenuProps, 'value'> & {
  value?: Option;
  nextValue?: Option;
  buttonProps?: ButtonProps;
  type?: 'select' | 'menu';
  children: ReactNode;
  onChange?: (value: Option['value']) => void;
  isLoading?: boolean;
  dropdownHotkey?: string;
};
export const QuickSelect = ({
  children,
  colorScheme = 'gray',
  value: incomingValue,
  nextValue,
  buttonProps,
  type = 'select',
  onChange,
  isLoading = false,
  dropdownHotkey,
  ...rest
}: Props) => {
  const [value, setValue] = useState(incomingValue as Option);

  const cs = useCallback((num: number) => `${colorScheme}.${num}`, [colorScheme]);

  const theme = useTheme();
  const { colorMode } = useColorMode();

  const handleQuickSelect = () => {
    // Update value of the select field
    setValue(nextValue);

    // Call the onChange function if it exists
    onChange?.(nextValue.value as string);
  };

  const handleOnChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = (children as any[]).find((child: any) => child.props.value === event.target.value).props;

    setValue(selectedValue);
    onChange?.(selectedValue.value as string);
  };

  // mode() cannot be inside useMemo(), so these all have to be factored out into variables.
  // This sadly hurts readability, but it also removes large react errors from the developer console.
  const bgMain = mode(cs(50), t(cs(100), 0.25)(theme));
  const colorMain = mode(cs(800), cs(200));
  const borderColorMain = mode(cs(200), cs(500));
  const bgActive = mode(cs(100), t(cs(500), 0.2)(theme));
  const bgFocus = mode(cs(100), t(cs(200), 0.2)(theme));
  const bgHover = mode(cs(100), t(cs(200), 0.25)(theme));
  const borderColorHover = mode(cs(300), cs(400));
  const bgDisabled = mode(cs(50), t(cs(50), 0.12)(theme));
  const props = useMemo(
    () => ({
      bg: bgMain,
      color: colorMain,
      borderColor: borderColorMain,
      borderWidth: 1,
      _active: {
        bg: bgActive,
        borderColor: cs(200),
      },
      _focus: {
        bg: bgFocus,
        borderColor: cs(200),
      },
      _hover: {
        bg: bgHover,
        borderColor: borderColorHover,

        _disabled: {
          bg: bgDisabled,
          borderColor: cs(200),
        },
      },
    }),
    // disabled to include colorMode so that the styles are updated when the colorMode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cs, theme, colorMode],
  );

  const DropdownComponent = type === 'select' ? Select : Menu;

  const id = useMemo(() => rest.id ?? crypto.randomUUID(), [rest.id]);

  useHotkeys(
    dropdownHotkey,
    () => {
      document.getElementById('menu-button-' + id).click();
    },
    { preventDefault: true, enabled: !!dropdownHotkey },
  );

  return (
    <Stack direction="row" spacing="0" flex={1}>
      <Button
        {...props}
        borderRightRadius="0"
        onClick={handleQuickSelect}
        {...buttonProps}
        borderColor={borderColorMain}
        noOfLines={1}
        display="flex"
        flex={1}
        alignItems="center"
        justifyContent="flex-start"
      >
        {nextValue?.label}
      </Button>
      <DropdownComponent
        {...rest}
        {...props}
        bg={buttonProps?.bg || bgMain}
        borderLeftRadius="0"
        borderLeftWidth="0"
        value={value?.value}
        style={{ paddingInlineStart: '0.5rem', width: 0 }}
        icon={isLoading ? <Spinner /> : undefined}
        onChange={handleOnChange}
        id={id}
      >
        {children}
      </DropdownComponent>
    </Stack>
  );
};
